import { useState } from "react";
import { Button, Dropdown, Tooltip } from "antd";
import { CheckOutlined, DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TrackOnClick } from "../../host/TrackOnClick";
import type { TeamMember } from "../types";
import { getPlanDisplayName } from "../planNames";
import { hasPersonalPlan } from "../memberPlanRules";
import { PlanBadge } from "./PlanBadge";
// Shares the popup/menu-item chrome with the role selector — the dropdown
// look is identical, only the options differ.
import styles from "./MemberRoleSelection.module.css";

export interface MemberLicenseSelectionProps {
  member: TeamMember;
  /** The team's current plan name (account.plan) — shown for a licensed
   *  member and as the "grant" option label. */
  teamPlan: string | null | undefined;
  /** Whether the viewer may grant/revoke this row's seat (owner/admin, paid
   *  plan, non-SCIM, not the owner row). When false the cell is read-only. */
  canEdit: boolean;
  /** AHA-43204: seats available on the team (quota - used > 0). Drives the
   *  Grant menu item — when false, granting is disabled with a tooltip so
   *  the user learns the team is at cap before hitting a server 400. Revoke
   *  stays enabled regardless. */
  canGrantMore: boolean;
  /** Grant/revoke API call in flight for this row — swaps the chevron for a
   *  spinner, matching MemberRoleSelection. */
  pending?: boolean;
  onChange: (licensed: boolean) => void;
}

export function MemberLicenseSelection({
  member,
  teamPlan,
  canEdit,
  canGrantMore,
  pending = false,
  onChange,
}: MemberLicenseSelectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Personal-plan member (AHA-41755): their Users.plan is a paid plan that
  // doesn't match the team's. Render their plan name read-only — the team
  // licence model doesn't apply to them (granting wouldn't gain them anything;
  // revoking has nothing to revoke).
  //
  // AHA-43204 (comment 167115): use the same PlanBadge component as the
  // licence meter so the table and meter render badges consistently. The
  // PlanBadge palette is keyword-driven, so personal vs. team plans land
  // on distinct colours automatically (e.g. pro/large vs. small/essential).
  if (hasPersonalPlan(member, teamPlan)) {
    return (
      <Tooltip title={t("team.license.personalPlanHint")}>
        <span data-testid="my-team-member-license-personal-plan">
          <PlanBadge plan={member.plan} />
        </span>
      </Tooltip>
    );
  }

  const licensed = Boolean(member.license);
  // AHA-43296 (demo feedback, comment 168990): the member table shows the
  // member's ACTUAL plan, not the "Enterprise" contract label — even for
  // enterprise teams. The "Enterprise" badge is kept only beside the
  // licence-usage meter (LicenseUsageMeter), where it labels the team as a
  // whole. People argued the per-member plan is more useful info here.
  const planLabel = getPlanDisplayName(teamPlan) || t("team.license.licensed");

  const label = licensed ? (
    <PlanBadge plan={teamPlan} />
  ) : (
    <span style={{ color: "var(--ant-color-text-tertiary)" }}>
      {t("team.license.unlicensed")}
    </span>
  );

  if (!canEdit) {
    return <span data-testid="my-team-member-license-view-only">{label}</span>;
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    if (typeof window === "undefined" || window.parent === window) return;
    try {
      window.parent.postMessage(
        {
          type: "userAction",
          eventName: "click",
          name: "my-team-member-license-trigger-button",
          properties: {},
        },
        "*",
      );
    } catch {
      /* never leak into the open/close handler */
    }
  };

  const pick = (next: boolean) => {
    setOpen(false);
    if (next === licensed) return;
    onChange(next);
  };

  return (
    <Dropdown
      trigger={["click"]}
      open={open}
      onOpenChange={handleOpenChange}
      popupRender={() => {
        // AHA-43204: when the row is already licensed, clicking "Grant"
        // is a no-op (pick() bails when next === licensed), so the cap
        // check only matters for unlicensed rows. The tooltip explains
        // the disabled state without surprising the licensed-row case.
        const grantBlocked = !licensed && !canGrantMore;
        const grantItem = (
          <TrackOnClick name="my-team-member-license-grant-menu-item">
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => pick(true)}
              disabled={grantBlocked}
              data-testid="my-team-member-license-grant-menu-item"
            >
              <CheckOutlined className={styles.checkIcon} data-visible={licensed} />
              {planLabel}
            </button>
          </TrackOnClick>
        );
        return (
          <div className={styles.popup}>
            {grantBlocked ? (
              // span wrapper because Tooltip can't fire hover on a disabled
              // button (pointer events get swallowed by the browser).
              <Tooltip title={t("team.toast.licenseCapExceeded")}>
                <span style={{ display: "block" }}>{grantItem}</span>
              </Tooltip>
            ) : (
              grantItem
            )}
            <TrackOnClick name="my-team-member-license-revoke-menu-item">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => pick(false)}
                data-testid="my-team-member-license-revoke-menu-item"
              >
                <CheckOutlined className={styles.checkIcon} data-visible={!licensed} />
                {t("team.license.unlicensed")}
              </button>
            </TrackOnClick>
          </div>
        );
      }}
    >
      <Button
        type="text"
        style={{ padding: "0 8px", fontWeight: "normal" }}
        disabled={pending}
        data-testid="my-team-member-license-trigger-button"
      >
        {label}
        {pending ? (
          <LoadingOutlined spin style={{ marginLeft: 4, fontSize: 12 }} />
        ) : (
          <DownOutlined style={{ marginLeft: 4, fontSize: 10 }} />
        )}
      </Button>
    </Dropdown>
  );
}
